/**
 * The "Network map" tab of a FortiGate: who talks through the firewall, in
 * columns. Internet, the firewall, its interfaces, the subnets behind the
 * interface I open, and the hosts of the subnet I open.
 *
 *   Component class:   FortinetNetmapComponent
 *   Selector:          app-fortinet-netmap
 *   Data:              document `session_traffic.bucket.netmap`
 *                      (dashboard.build_netmap), plus `ports` and
 *                      `iface_latest` for the interface icons and link state
 *
 * Every object that saw traffic (the firewall, an interface, a subnet, a
 * host) has a small round badge with its number of policies: a click on
 * it opens a card with the firewall policies of the sessions it was part
 * of. A click on a busiest host opens it too.
 *
 * It's built to stay readable on a big site: only one interface and one
 * subnet are open at a time, and whatever isn't drawn one by one is grouped
 * in a dashed "+N" node that keeps its volume. So the line widths always add
 * up, whether the device sees 50 hosts or 10,000.
 *
 * It's plain SVG drawn by the template (no ECharts): the positions are
 * computed here, in columns, so nothing moves between two refreshes.
 *
 * The "+N other hosts" node opens a Clarity side panel with every host of
 * the subnet, one page at a time, from GET /hosts.json. Only the page shown
 * is downloaded, so it works the same with 200 or 200,000 hosts.
 *
 * What is open and pinned is saved on the server for each device (POST
 * /netmap/state, file dashboard_netmap_state.json), like the issue
 * acknowledgements: it survives a reload and is shared by every browser.
 */
import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import {
  ClrDatagridModule,
  ClrDatagridPagination,
  ClrDatagridSortOrder,
  ClrDatagridStateInterface,
  ClrSidePanelModule,
  ClrSpinnerModule,
} from '@clr/angular';
import { Subscription } from 'rxjs';

import { describeApiError, fmtBps, fmtBytes, fmtNum } from './fortinet-shared';
import { environment } from '../../environments/environment';
import { PortsDoc } from './fortinet-interfaces.component';

/* ============================================================================
 * Shape of the document (dashboard.build_netmap)
 * ========================================================================== */

/** One firewall policy the sessions went through (dashboard.top_policies). */
export interface NetmapPolicy {
  /** policy id, as in the policy list of the FortiGate */
  id: number;
  /** '' when the policy has no name (or the cmdb snapshot isn't there yet) */
  name: string;
  bytes: number;
  sessions: number;
  /** egress interfaces (dstintf), busiest first */
  to: string[];
}

/** The busiest policies of a level of the map, and what was left out. */
export interface NetmapPolicies {
  top: NetmapPolicy[];
  other: number;
  other_bytes: number;
}

export interface NetmapHost {
  ip: string;
  /** DHCP host name, '' when unknown */
  name: string;
  bytes: number;
  /** laptop, server, phone, printer, camera, ap, cloud or unknown (a guess) */
  kind: string;
  /** absent from an older dashboard.py */
  policies?: NetmapPolicies;
}

export interface NetmapSubnet {
  /** "10.0.12.0/24", "public addresses" or "other" */
  cidr: string;
  bytes: number;
  hosts: number;
  /** the busiest hosts only */
  top: NetmapHost[];
  other_hosts: number;
  other_bytes: number;
  /** absent from an older dashboard.py, and on the subnets it left out */
  policies?: NetmapPolicies;
}

export interface NetmapInterface {
  name: string;
  bytes: number;
  hosts: number;
  subnets: NetmapSubnet[];
  /** subnets left out of the document (too many) */
  other_subnets: number;
  other_subnets_bytes: number;
  /** absent from an older dashboard.py */
  policies?: NetmapPolicies;
}

export interface NetmapDoc {
  total_bytes: number;
  total_hosts: number;
  interfaces: NetmapInterface[];
  /** every policy of the device (absent from an older dashboard.py) */
  policies?: NetmapPolicies;
}

/** What is open and pinned on the map of one device, as saved on the
 *  server (document `netmap_state`, POST /netmap/state). */
export interface NetmapState {
  open_interface: string | null;
  open_subnet: string | null;
  show_all_interfaces: boolean;
  show_all_subnets: boolean;
  pinned: HostRow | null;
  /** epoch ms of the save, set by the server */
  updated_at?: number;
}

/** One host of GET /hosts.json. */
export interface HostRow {
  ip: string;
  name: string;
  bytes: number;
  kind: string;
  interface: string;
  subnet: string;
  /** 1 for the busiest host of the selection, whatever the sort (absent
   *  from an older dashboard.py) */
  rank?: number;
  /** its busiest policies (absent from an older dashboard.py; never saved
   *  with the map state, the server drops it) */
  policies?: NetmapPolicies;
}

/** One page of GET /hosts.json (dashboard.hosts_page). */
export interface HostsPage {
  total: number;
  offset: number;
  limit: number;
  /** the busiest host of the whole selection, for the bars */
  max_bytes: number;
  /** epoch ms of the build the list comes from */
  built_at: number;
  rows: HostRow[];
}

/** The few fields of iface_latest this map reads. */
export interface NetmapLink {
  name: string;
  link: boolean;
  rx_bps: number | null;
  tx_bps: number | null;
}

/* ============================================================================
 * View models
 * ========================================================================== */

type NodeLevel = 'iface' | 'subnet' | 'host';
/** normal, open (on the path I opened), down (link down), group (a dashed
 *  "+N" node) or found (the search result) */
type NodeState = 'normal' | 'open' | 'down' | 'group' | 'found';

interface MapNode {
  /** unique within the map, used by trackBy */
  key: string;
  level: NodeLevel;
  /** what a click does: the interface or subnet name, or a group id */
  target: string;
  x: number;
  y: number;
  width: number;
  label: string;
  sub: string;
  volume: string;
  /** icon symbol id, without the uid prefix */
  icon: string;
  state: NodeState;
  /** what a click does, in words: "expand", "show all", ... ('' for none) */
  action: string;
  /** the small sign drawn for the action: › expand, ‹ collapse, + or − */
  mark: string;
  /** label and sub, cut with … when they don't fit in the pill */
  labelShown: string;
  subShown: string;
  /** the full text, shown as a tooltip on hover */
  title: string;
  /** what a screen reader says for it */
  aria: string;
  /** the policies of the sessions it was part of (undefined: nothing to show) */
  policies?: NetmapPolicies;
  /** the interface the traffic came in through, for the policy card */
  policyFrom?: string;
  /** what the policy card calls it: "Interface port2", "Host pc-12 (10.0.1.5)" */
  policyTitle?: string;
}

interface MapEdge {
  key: string;
  path: string;
  width: number;
  state: NodeState;
}

/** The round badge with the number of policies, on the top right corner
 *  of an object. */
interface PolicyBadge {
  /** the key of its node (or FIREWALL_KEY) */
  key: string;
  x: number;
  y: number;
  /** "3", or "9+" */
  text: string;
  aria: string;
}

/** The card opened by a badge. */
interface PolicyCard {
  key: string;
  title: string;
  rows: PolicyRow[];
  /** "+3 other policies, 1.2 GB" ('' when none) */
  otherText: string;
  /** why the list is empty ('' when it isn't) */
  note: string;
  /** where it sits over the map, in px (the SVG is drawn 1 unit = 1 px) */
  left: number;
  top: number;
  /** true: the card is above its badge (the object is low on the map) */
  above: boolean;
  maxHeight: number;
}

/** One line of the policy card. */
interface PolicyRow {
  key: string;
  /** "#12" */
  idText: string;
  /** '' when the policy has no name */
  name: string;
  /** "port2 → wan1", or "to wan1" for the whole device */
  route: string;
  /** the same with every egress interface, for the tooltip */
  routeTitle: string;
  sessions: string;
  volume: string;
  /** width of the volume bar, 0 to 100 */
  bar: number;
}

/* ---- layout, in SVG units ----------------------------------------------- */
const X_INTERNET = 60;
const X_FIREWALL = 215;
const X_IFACE = 372;
const WIDTH_IFACE = 180;
const X_SUBNET = 626;
const WIDTH_SUBNET = 210;
const X_HOST = 900;
const WIDTH_HOST = 260;
const MAP_WIDTH = 1180;
const TOP = 60;
/** The firewall box is 88 high: its centre never goes above this, so the
 *  box stays under the column titles. */
const FIREWALL_MIN_Y = 90;
/** Text inside a pill starts after the icon, and keeps a margin on the right. */
const TEXT_LEFT = 40;
const TEXT_RIGHT = 12;
/** Room kept on the right of the first line for the action sign. */
const MARK_ROOM = 18;
/** Rough width of one character, in SVG units: an estimate is enough to
 *  decide where to cut (the full text is in the tooltip anyway). */
const CHAR_WIDTH_LABEL = 7.4;
const CHAR_WIDTH_SUB = 6.2;
const CHAR_WIDTH_MODEL = 6.0;
/** The firewall box, centred on X_FIREWALL. */
const FIREWALL_WIDTH = 120;
const ROW = 64;
/** the widest line, for the busiest item of a column */
const MAX_LINE = 9;

/* ---- how much is drawn one by one before grouping ----------------------- */
const INTERFACES_SHOWN = 6;
const SUBNETS_SHOWN = 5;

const GROUP_INTERFACES = '#more-interfaces';
const GROUP_SUBNETS = '#more-subnets';
const GROUP_HOSTS = '#more-hosts';
const UNPIN = '#unpin';
/** Action word of a busiest host: a click opens its policy card. */
const ACTION_HOST_POLICIES = 'show its policies';
/** How many egress interfaces are written in a policy line before "+N". */
const ROUTE_NAMES_SHOWN = 2;
/** The badge key of the firewall box (the policies of the whole device). */
const FIREWALL_KEY = '#firewall';
/** The policy card, in px. */
const CARD_WIDTH = 480;
const CARD_MAX_HEIGHT = 300;
/** Space between the badge and the card. */
const CARD_GAP = 12;
/** Rows per page of the host list, and the sizes I can switch to. */
const HOSTS_PAGE_SIZE = 50;

/** The order of the host list, in words, for the panel title. */
function hostOrderText(sortBy: string, reverse: boolean): string {
  // rank 1 is the busiest, so rank A to Z and bytes Z to A are the same
  const busiestFirst = (sortBy === 'rank' && !reverse) || (sortBy === 'bytes' && reverse);
  if (sortBy === 'rank' || sortBy === 'bytes') {
    return busiestFirst ? 'busiest first' : 'quietest first';
  }
  if (sortBy === 'ip') {
    return reverse ? 'by IP, last first' : 'by IP';
  }
  return reverse ? 'by host name, Z to A' : 'by host name';
}

/** Where the firewall sits: in front of its interfaces, never under the
 *  column titles. */
function firewallYFor(interfaceRows: number): number {
  const middle = TOP + ((interfaceRows - 1) * ROW) / 2;
  return Math.max(FIREWALL_MIN_Y, middle);
}

/** Cuts a text with … when it's longer than the room, in SVG units. */
function fitText(text: string, room: number, charWidth: number): string {
  const maxChars = Math.floor(room / charWidth);
  if (text.length <= maxChars) return text;
  if (maxChars <= 1) return '…';
  return text.slice(0, maxChars - 1) + '…';
}

/** The sign drawn for an action word. */
function markOf(action: string): string {
  if (action === 'expand') return '›';
  if (action === 'collapse') return '‹';
  if (action === 'show all') return '+';
  if (action === 'show less') return '−';
  if (action === 'unpin') return '×';
  return '';
}

/** "1 host", "12 hosts" */
function hostCount(count: number): string {
  return `${fmtNum(count)} ${count === 1 ? 'host' : 'hosts'}`;
}

/** "wan1", "wan1, wan2", or "wan1, wan2 +3" when there are more than
 *  `maxShown` names. */
function namesText(names: string[], maxShown: number): string {
  if (names.length <= maxShown) {
    return names.join(', ');
  }
  const shown = names.slice(0, maxShown).join(', ');
  const hiddenCount = names.length - maxShown;
  return `${shown} +${hiddenCount}`;
}

/** "#12 LAN to WAN", or "#12" when the policy has no name. */
function policyText(policy: NetmapPolicy): string {
  if (policy.name) {
    return `#${policy.id} ${policy.name}`;
  }
  return `#${policy.id}`;
}

/** The busiest policy of a host, in a few words for its tooltip:
 *  ", policy #12 LAN to WAN" ('' when unknown). */
function mainPolicyText(policies: NetmapPolicies | undefined): string {
  if (!policies || !policies.top.length) {
    return '';
  }
  const busiest = policies.top[0];
  const othersCount = policies.top.length - 1 + policies.other;
  let text = `, policy ${policyText(busiest)}`;
  if (othersCount > 0) {
    text += ` (+${othersCount})`;
  }
  return text;
}

/** A curve going right, from one column to the next. */
function curve(x1: number, y1: number, x2: number, y2: number): string {
  const middle = (x1 + x2) / 2;
  return `M${x1},${y1} C${middle},${y1} ${middle},${y2} ${x2},${y2}`;
}

/** Line width for a volume, compared to the busiest of its column. */
function lineWidth(bytes: number, maxBytes: number): number {
  if (maxBytes <= 0) return 1.5;
  return 1.5 + MAX_LINE * (bytes / maxBytes);
}

/** "10.0.12.0/24" -> [first address, last address] as numbers, IPv4 only.
 *  null for anything else (IPv6, "public addresses", ...). */
function ipv4Range(cidr: string): [number, number] | null {
  const match = /^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/.exec(cidr);
  if (!match) return null;
  const start = ipv4Number(`${match[1]}.${match[2]}.${match[3]}.${match[4]}`);
  const prefix = Number(match[5]);
  if (start == null || prefix > 32) return null;
  const size = Math.pow(2, 32 - prefix);
  return [start, start + size - 1];
}

/** "10.0.12.7" -> a number I can compare, or null. */
function ipv4Number(text: string): number | null {
  const parts = text.split('.');
  if (parts.length !== 4) return null;
  let value = 0;
  for (const part of parts) {
    if (!/^\d+$/.test(part)) return null;
    const byte = Number(part);
    if (byte > 255) return null;
    // * 256 instead of << 8, so it never turns negative
    value = value * 256 + byte;
  }
  return value;
}

@Component({
  selector: 'app-fortinet-netmap',
  standalone: true,
  imports: [CommonModule, ClrSidePanelModule, ClrDatagridModule, ClrSpinnerModule],
  templateUrl: './fortinet-netmap.component.html',
  // The dashboard stylesheet brings the Clarity look (cards, labels), this
  // one only adds the map.
  styleUrls: ['./fortinet-dashboard.component.css',
              './fortinet-netmap.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FortinetNetmapComponent implements OnChanges, OnDestroy {
  @Input() netmap: NetmapDoc | null = null;
  /** for hosts.json: the device and history window shown, and the base URL
   *  of the Fortinet routes of the NetScope API */
  @Input() serial: string | null = null;
  @Input() range: string | null = null;
  @Input() apiBase = environment.fortinetApiUrl;
  /** the map state saved for this device, null when never saved */
  @Input() savedState: NetmapState | null = null;
  /** a new state was saved (the server's copy, with its updated_at) */
  @Output() stateSaved = new EventEmitter<NetmapState>();
  @Input() ports: PortsDoc | null = null;
  @Input() links: NetmapLink[] = [];
  @Input() deviceTitle = 'FortiGate';
  @Input() model: string | null = null;
  /** true while the dashboard loads another history window: the map on
   *  screen is still the one of the previous window */
  @Input() loading = false;
  /** which sessions the map is built from, "Sessions of ... to ..., 78
   *  polls" ('' when unknown), and whether the budget cut the window short */
  @Input() coverage = '';
  @Input() coverageCut = false;

  /** unique prefix for the SVG symbol ids of this instance */
  readonly uid = 'fgm-' + Math.random().toString(36).slice(2, 8);
  readonly mapWidth = MAP_WIDTH;

  // ---- what the template draws ----
  nodes: MapNode[] = [];
  edges: MapEdge[] = [];
  /** the round badges with the number of policies */
  policyBadges: PolicyBadge[] = [];
  mapHeight = 400;
  firewallY = 200;
  internetY = 120;
  /** device title and model, cut to fit the firewall box */
  firewallLabel = '';
  modelLabel = '';
  totalText = '';

  // ---- policy card ----
  /** the key of the object whose policy card is open (null: closed) */
  openPolicyKey: string | null = null;
  /** the card itself, rebuilt with the map (null: closed) */
  policyCard: PolicyCard | null = null;
  @ViewChild('policyClose') private policyCloseButton?: ElementRef<HTMLButtonElement>;
  /** the element that opened the policy card (see closePolicyCardByUser) */
  private policyOpener: HTMLElement | null = null;
  /** for the template: the target of the "+N other hosts" object */
  readonly groupHostsTarget = GROUP_HOSTS;

  // ---- what I opened (kept across refreshes when it still exists) ----
  openInterface: string | null = null;
  openSubnet: string | null = null;
  showAllInterfaces = false;
  showAllSubnets = false;
  /** so the busiest interface opens by itself only the first time */
  private autoOpened = false;
  /** the device whose state is on screen: when it changes, I start over
   *  from that device's saved state (undefined: nothing restored yet) */
  private restoredFor: string | null | undefined = undefined;
  /** says when the map state couldn't be saved */
  stateMessage = '';
  private saveRequest: Subscription | null = null;

  // ---- search ----
  query = '';
  /** the IP or subnet the search found, drawn in green */
  found: string | null = null;
  searchMessage = '';
  /** a host added to the map by "Show on map" or by the search, even when
   *  it isn't among the busiest of its subnet */
  pinned: HostRow | null = null;
  /** the pinned host is not a source address in this window any more */
  pinnedMissing = false;
  private pinnedRequest: Subscription | null = null;

  // ---- side panel: every host of a subnet, page by page ----
  panelOpen = false;
  panelInterface = '';
  panelSubnet = '';
  panelQuery = '';
  panelRows: HostRow[] = [];
  panelTotal = 0;
  panelOffset = 0;
  panelMaxBytes = 0;
  panelLoading = false;
  panelError = '';
  /** says when the ranking changed between two pages */
  panelNote = '';
  readonly pageSize = HOSTS_PAGE_SIZE;
  readonly pageSizes = [25, 50, 100, 200];
  /** The column the list is sorted on ('rank', 'bytes', 'ip' or 'name', the
   *  clrDgSortBy of the columns) and its direction. The datagrid's own
   *  sort headers set them; the server does the sorting. */
  private panelSortBy = 'rank';
  private panelSortReverse = false;
  /** "busiest first", "by IP", ... under the panel title */
  panelOrderText = 'busiest first';
  /** the # column shows as sorted when the panel opens, because the list
   *  arrives busiest first */
  readonly rankSortOrder = ClrDatagridSortOrder.ASC;
  private panelBuiltAt = 0;
  /** keeps panelNote for one more load (see loadPanel) */
  private panelKeepNote = false;
  private panelRequest: Subscription | null = null;
  private searchRequest: Subscription | null = null;

  @ViewChild(ClrDatagridPagination) private pagination?: ClrDatagridPagination;

  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges): void {
    // Another device (or the first data): everything I had open belongs to
    // the previous device, so I forget it and take this device's saved
    // state. Without this, a host pinned on one device could show up on
    // another one that has an interface and a subnet with the same names.
    if (changes['serial'] || this.restoredFor === undefined) {
      if (this.serial !== this.restoredFor) {
        this.restoreState();
      }
    }

    const netmap = this.netmap;
    if (netmap && !this.autoOpened && netmap.interfaces.length) {
      // I open the busiest interface and its busiest subnet, so the first
      // view already shows something useful
      const busiest = netmap.interfaces[0];
      this.openInterface = busiest.name;
      this.openSubnet = busiest.subnets.length ? busiest.subnets[0].cidr : null;
      this.autoOpened = true;
    }

    // The pinned host carries the volume it had when I pinned it (that's
    // what the saved state holds). With new data I fetch its volume again,
    // otherwise it shows an old number next to fresh ones.
    if (changes['netmap'] && this.pinned) {
      this.refreshPinned();
    }
    this.build();
  }

  ngOnDestroy(): void {
    this.panelRequest?.unsubscribe();
    this.searchRequest?.unsubscribe();
    this.pinnedRequest?.unsubscribe();
    // a save still running is NOT cancelled: it must reach the server
  }

  /* -------------------------------------------------------- saved state */

  /** Starts over for the current device, from its saved state if any. */
  private restoreState(): void {
    this.restoredFor = this.serial;
    this.openInterface = null;
    this.openSubnet = null;
    this.showAllInterfaces = false;
    this.showAllSubnets = false;
    this.pinned = null;
    this.pinnedMissing = false;
    this.pinnedRequest?.unsubscribe();
    this.openPolicyKey = null;
    this.found = null;
    this.query = '';
    this.searchMessage = '';
    this.stateMessage = '';
    this.panelOpen = false;
    this.autoOpened = false;

    const saved = this.savedState;
    if (saved) {
      this.openInterface = saved.open_interface;
      this.openSubnet = saved.open_subnet;
      this.showAllInterfaces = saved.show_all_interfaces;
      this.showAllSubnets = saved.show_all_subnets;
      this.pinned = saved.pinned;
      this.found = saved.pinned ? saved.pinned.ip : null;
      // a saved state wins over opening the busiest interface, even when
      // it says "everything closed"
      this.autoOpened = true;
    }
  }

  /** Sends what is open and pinned to the server. Called after every
   *  change I make on the map. */
  private saveState(): void {
    if (!this.serial) return;
    const state: NetmapState = {
      open_interface: this.openInterface,
      open_subnet: this.openSubnet,
      show_all_interfaces: this.showAllInterfaces,
      show_all_subnets: this.showAllSubnets,
      pinned: this.pinned,
    };
    const body = { serial: this.serial, state };
    // only the last click counts: an older save still running is dropped
    this.saveRequest?.unsubscribe();
    this.saveRequest = this.http.post<{ ok: boolean; state: NetmapState }>(
      `${this.apiBase}/netmap/state`, body)
      .subscribe({
        next: (answer) => {
          this.stateMessage = '';
          this.stateSaved.emit(answer.state);
          this.cdr.markForCheck();
        },
        error: () => {
          this.stateMessage = 'The map could not be saved on the server: it will be '
            + 'back to its last saved state after a reload.';
          this.cdr.markForCheck();
        },
      });
  }

  /** Takes the volume and the policies of the pinned host again, from the
   *  new document or from the server. Not saved: it's only for display,
   *  the saved state just needs to know which host is pinned. */
  private refreshPinned(): void {
    const pinned = this.pinned;
    const netmap = this.netmap;
    if (!pinned || !netmap) return;

    // 1. it's among the busiest hosts of the document: its numbers are there
    const iface = netmap.interfaces.find((item) => item.name === pinned.interface);
    const subnet = iface?.subnets.find((item) => item.cidr === pinned.subnet);
    const host = subnet?.top.find((item) => item.ip === pinned.ip);
    if (host) {
      this.pinned = { ...pinned, name: host.name, kind: host.kind, bytes: host.bytes,
                      policies: host.policies };
      this.pinnedMissing = false;
      return;
    }

    // 2. otherwise ask the server, which knows every host
    if (!this.serial) return;
    this.pinnedRequest?.unsubscribe();
    // The "q" filter keeps the IPs that CONTAIN the text ("10.0.0.1" also
    // finds 10.0.0.10 to 10.0.0.199). Sorted by IP, the exact one comes
    // first, so it's always in the page.
    this.pinnedRequest = this.fetchHosts(pinned.interface, pinned.subnet, pinned.ip,
                                         0, 20, 'ip', false).subscribe({
      next: (page) => {
        // the user may have pinned another host in the meantime
        if (this.pinned?.ip !== pinned.ip) return;
        const row = page.rows.find((item) => item.ip === pinned.ip);
        if (row) {
          this.pinned = row;
          this.pinnedMissing = false;
        } else {
          // Not seen in this window any more: I keep it pinned (it's the
          // user's choice) but it moved nothing.
          this.pinned = { ...pinned, bytes: 0, policies: undefined };
          this.pinnedMissing = true;
        }
        this.build();
        this.cdr.markForCheck();
      },
      // the server can't answer: the map keeps the saved numbers
      error: () => undefined,
    });
  }

  /* ------------------------------------------------------------- actions */

  onNode(node: MapNode): void {
    if (node.level === 'host') {
      if (node.target === GROUP_HOSTS) {
        this.openPanel();
      } else if (node.target === UNPIN) {
        this.pinned = null;
        this.pinnedMissing = false;
        this.found = null;
        this.build();
        this.saveState();
      } else if (node.target) {
        // a busiest host: a click opens (or closes) its policy card
        this.togglePolicyCard(node.key);
      }
      return;
    }
    if (node.level === 'iface') {
      if (node.target === GROUP_INTERFACES) {
        this.showAllInterfaces = !this.showAllInterfaces;
      } else if (this.openInterface === node.target) {
        // clicking the open one closes it
        this.openInterface = null;
        this.openSubnet = null;
      } else {
        this.openInterface = node.target;
        this.openSubnet = null;
        this.showAllSubnets = false;
      }
    } else if (node.level === 'subnet') {
      if (node.target === GROUP_SUBNETS) {
        this.showAllSubnets = !this.showAllSubnets;
      } else if (this.openSubnet === node.target) {
        this.openSubnet = null;
      } else {
        this.openSubnet = node.target;
      }
    }
    this.build();
    this.saveState();
  }

  /** A click on a policy badge: opens its card, or closes it when it's
   *  the one already open. Not saved (it's only a look). */
  togglePolicyCard(key: string): void {
    if (this.openPolicyKey === key) {
      this.closePolicyCard();
      return;
    }
    // what had the focus (the badge or the host), to give it back on close
    this.policyOpener = document.activeElement as HTMLElement | null;
    this.openPolicyKey = key;
    this.build();
    this.cdr.markForCheck();
    // The card is drawn at the next change detection. The timeout waits for
    // it, then puts the keyboard focus on its close button, so a keyboard
    // user lands in the card and Escape closes it.
    setTimeout(() => this.policyCloseButton?.nativeElement.focus());
  }

  /** The user closed the policy card (its × button or Escape): the focus
   *  goes back to the badge or host that opened it, as the APG dialog
   *  pattern asks. Without it, the focus was lost with the card, and a
   *  keyboard user started again from the top of the page. */
  closePolicyCardByUser(): void {
    const opener = this.policyOpener;
    this.closePolicyCard();
    if (opener && opener.isConnected) {
      opener.focus();
    }
  }

  /** Is this map object open? For aria-expanded of the objects that open
   *  and close something: an interface (its subnets), a subnet (its hosts),
   *  a "+N" group (shows all), a host (its policy card). null for the
   *  others (the "+N other hosts" opens the side panel instead, and says
   *  so with aria-haspopup). */
  nodeExpanded(node: MapNode): boolean | null {
    if (node.level === 'iface') {
      if (node.target === GROUP_INTERFACES) {
        return this.showAllInterfaces;
      }
      return this.openInterface === node.target;
    }
    if (node.level === 'subnet') {
      if (node.target === GROUP_SUBNETS) {
        return this.showAllSubnets;
      }
      return this.openSubnet === node.target;
    }
    if (node.target === GROUP_HOSTS || node.target === UNPIN) {
      return null;
    }
    return this.openPolicyKey === node.key;
  }

  closePolicyCard(): void {
    this.openPolicyKey = null;
    this.policyCard = null;
    this.cdr.markForCheck();
  }

  /** Enter or space on a badge, for keyboard users. */
  onBadgeKey(event: KeyboardEvent, key: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.togglePolicyCard(key);
    }
  }

  /** Escape closes the policy card, wherever the focus is. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.openPolicyKey) {
      this.closePolicyCardByUser();
    }
  }

  /** Enter or space on a node, for keyboard users. */
  onNodeKey(event: KeyboardEvent, node: MapNode): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onNode(node);
    }
  }

  /** Breadcrumb: back to the interfaces, or to the open interface. */
  closeTo(level: 'device' | 'iface'): void {
    if (level === 'device') {
      this.openInterface = null;
    }
    this.openSubnet = null;
    this.build();
    this.saveState();
  }

  setQuery(text: string): void {
    this.query = text;
  }

  clearSearch(): void {
    this.searchRequest?.unsubscribe();
    this.query = '';
    this.found = null;
    const hadPin = this.pinned !== null;
    this.pinned = null;
    this.pinnedMissing = false;
    this.pinnedRequest?.unsubscribe();
    this.searchMessage = '';
    this.build();
    if (hadPin) {
      this.saveState();
    }
  }

  /** Finds a host (IP or DHCP name) or a subnet and opens the path to it. */
  search(): void {
    const netmap = this.netmap;
    const query = this.query.trim().toLowerCase();
    this.found = null;
    this.searchMessage = '';
    if (!netmap || !query) {
      this.build();
      return;
    }

    // 1. a host drawn one by one somewhere (exact IP, or part of its name)
    for (const iface of netmap.interfaces) {
      for (const subnet of iface.subnets) {
        for (const host of subnet.top) {
          const nameMatches = host.name !== '' && host.name.toLowerCase().includes(query);
          if (host.ip === query || nameMatches) {
            // pinned like a server result, so it's saved and comes back
            this.showOnMap({ ip: host.ip, name: host.name, bytes: host.bytes, kind: host.kind,
                             interface: iface.name, subnet: subnet.cidr,
                             policies: host.policies });
            return;
          }
        }
      }
    }

    // 2. ask the server, which knows every host (not only the busiest)
    if (this.serial) {
      this.searchOnServer(query);
      return;
    }
    this.searchInSubnets(query);
  }

  /** Search step 2: GET /hosts.json?q=..., then pin the best match. */
  private searchOnServer(query: string): void {
    this.searchRequest?.unsubscribe();
    this.searchMessage = 'Searching every host…';
    this.cdr.markForCheck();

    // A full IPv4 address: I want that exact host. The server keeps every IP
    // that CONTAINS the text, busiest first, so with 20 rows the exact one
    // could be missing ("10.0.0.1" also matches 10.0.0.10 to 10.0.0.199).
    // Sorted by IP, the exact one always comes first.
    const isAddress = ipv4Number(query) != null;
    let sortBy = 'rank';
    if (isAddress) {
      sortBy = 'ip';
    }

    this.searchRequest = this.fetchHosts('', '', query, 0, 20, sortBy, false).subscribe({
      next: (page) => {
        // an exact IP match wins, otherwise the busiest match
        let best: HostRow | null = page.rows.length ? page.rows[0] : null;
        for (const row of page.rows) {
          if (row.ip === query) {
            best = row;
            break;
          }
        }
        // an address that isn't a source host: its subnet, not a neighbour
        if (isAddress && best && best.ip !== query) {
          best = null;
        }
        if (best) {
          this.searchMessage = page.total > 1 && !isAddress
            ? `${page.total} hosts match, showing the busiest one. Open "other hosts" to see them all.`
            : '';
          this.showOnMap(best);
        } else {
          // the server knows every host, so this one really isn't there
          this.searchInSubnets(query, true);
        }
        this.cdr.markForCheck();
      },
      // the server can't answer (older dashboard.py): search what I have
      error: () => {
        this.searchInSubnets(query);
        this.cdr.markForCheck();
      },
    });
  }

  /** Search step 3: the subnet holding the address. notSeen is true when
   *  the server (which knows every host) didn't find it. */
  private searchInSubnets(query: string, notSeen = false): void {
    const netmap = this.netmap;
    if (!netmap) return;
    // a subnet, by its text ("10.0.12.0/24") or holding the IPv4 address
    const address = ipv4Number(query);
    for (const iface of netmap.interfaces) {
      for (const subnet of iface.subnets) {
        let holdsIt = subnet.cidr.toLowerCase() === query;
        if (!holdsIt && address != null) {
          const range = ipv4Range(subnet.cidr);
          holdsIt = range != null && address >= range[0] && address <= range[1];
        }
        if (holdsIt) {
          this.openPath(iface, subnet);
          this.found = subnet.cidr;
          this.saveState();
          if (address != null && notSeen) {
            this.searchMessage = `${this.query.trim()} is not a source address in this window. `
              + `Its subnet is ${subnet.cidr} on ${iface.name}.`;
          } else if (address != null) {
            this.searchMessage = `${this.query.trim()} is in ${subnet.cidr} on ${iface.name}, `
              + 'but not among its busiest hosts: it is counted in "other hosts".';
          }
          this.build();
          return;
        }
      }
    }

    this.searchMessage = 'Not seen in this window. The map only knows the source '
      + 'addresses of the sessions.';
    this.build();
  }

  /** Adds a host to the map and opens the path to it. */
  showOnMap(row: HostRow): void {
    this.pinnedRequest?.unsubscribe();
    this.pinned = row;
    this.pinnedMissing = false;
    this.found = row.ip;
    this.openInterface = row.interface;
    this.openSubnet = row.subnet;
    const netmap = this.netmap;
    if (netmap) {
      const iface = netmap.interfaces.find((item) => item.name === row.interface);
      if (iface && netmap.interfaces.indexOf(iface) >= INTERFACES_SHOWN) {
        this.showAllInterfaces = true;
      }
      const subnetIndex = iface ? iface.subnets.findIndex((item) => item.cidr === row.subnet) : -1;
      this.showAllSubnets = subnetIndex >= SUBNETS_SHOWN;
    }
    this.panelOpen = false;
    this.build();
    this.cdr.markForCheck();
    this.saveState();
  }

  /* ---------------------------------------------------------- side panel */

  /** "+N other hosts": the list of every host of the open subnet. */
  private openPanel(): void {
    this.panelInterface = this.openInterface ?? '';
    this.panelSubnet = this.openSubnet ?? '';
    this.panelQuery = '';
    this.panelRows = [];
    this.panelTotal = 0;
    this.panelNote = '';
    this.panelError = '';
    this.panelBuiltAt = 0;
    this.panelKeepNote = false;
    this.panelSortBy = 'rank';
    this.panelSortReverse = false;
    this.panelOrderText = 'busiest first';
    // The datagrid is created when the panel opens (see the *ngIf in the
    // template), and asks for its first page by itself (clrDgRefresh).
    this.panelOpen = true;
    this.cdr.markForCheck();
  }

  /** The panel closed itself (its × button, Escape, a click outside). */
  onPanelOpenChange(open: boolean): void {
    if (!open) {
      this.closePanel();
    }
  }

  closePanel(): void {
    this.panelOpen = false;
    this.panelRequest?.unsubscribe();
  }

  setPanelQuery(text: string): void {
    this.panelQuery = text;
  }

  /** Search in the panel: back to page 1 with the new filter. */
  searchPanel(): void {
    if (this.pagination && this.pagination.currentPage !== 1) {
      // changing the page makes the datagrid call refreshPanel itself
      this.pagination.currentPage = 1;
    } else {
      this.loadPanel(0, this.pagination?.pageSize ?? HOSTS_PAGE_SIZE);
    }
  }

  /** The datagrid wants a page (first display, page or page size change,
   *  or a click on a column title). */
  refreshPanel(state: ClrDatagridStateInterface): void {
    const size = state.page?.size ?? HOSTS_PAGE_SIZE;
    const current = state.page?.current ?? 1;

    // The datagrid has its own sort headers (clrDgSortBy in the template),
    // but it can only sort the rows it has, and I only give it one page.
    // So I just read which column was clicked, and the server sorts the
    // whole list. With clrDgSortBy set to a text, state.sort.by is that text.
    let sortBy = 'rank';
    let reverse = false;
    if (state.sort && typeof state.sort.by === 'string') {
      sortBy = state.sort.by;
      reverse = state.sort.reverse;
    }
    const sortChanged = sortBy !== this.panelSortBy || reverse !== this.panelSortReverse;
    this.panelSortBy = sortBy;
    this.panelSortReverse = reverse;
    this.panelOrderText = hostOrderText(sortBy, reverse);

    // A new order starts from page 1: page 3 of another order means nothing.
    // Changing the page makes the datagrid call refreshPanel again.
    if (sortChanged && current !== 1 && this.pagination) {
      this.pagination.currentPage = 1;
      return;
    }
    this.loadPanel((current - 1) * size, size);
  }

  /** The number in the # column: the rank by volume sent by the server,
   *  or the position in the list with an older dashboard.py. */
  rankOf(row: HostRow, index: number): number {
    if (row.rank != null) {
      return row.rank;
    }
    return this.panelOffset + index + 1;
  }

  private loadPanel(offset: number, limit: number): void {
    this.panelRequest?.unsubscribe();
    this.panelLoading = true;
    this.panelError = '';
    this.cdr.markForCheck();
    this.panelRequest = this.fetchHosts(this.panelInterface, this.panelSubnet,
                                        this.panelQuery.trim(), offset, limit,
                                        this.panelSortBy, this.panelSortReverse).subscribe({
      next: (page) => {
        // The list comes from the last build of the document. If a newer
        // build happened while I was paging, the ranking may have moved:
        // I say it and go back to page 1 instead of mixing two rankings.
        const rebuilt = this.panelBuiltAt !== 0 && page.built_at !== this.panelBuiltAt;
        this.panelBuiltAt = page.built_at;
        if (rebuilt && offset > 0 && this.pagination) {
          this.panelNote = 'The data was updated, back to page 1.';
          // the page 1 load that follows must not erase this note
          this.panelKeepNote = true;
          this.pagination.currentPage = 1;
          return;
        }
        // Before, an update seen on page 1 said nothing (the volumes just
        // changed), and the "back to page 1" note was erased by the very
        // next load, so it was never seen either.
        if (rebuilt) {
          this.panelNote = 'The data was updated: the volumes are the new ones.';
        } else if (this.panelKeepNote) {
          this.panelKeepNote = false;
        } else {
          this.panelNote = '';
        }
        this.panelRows = page.rows;
        this.panelTotal = page.total;
        this.panelOffset = page.offset;
        this.panelMaxBytes = page.max_bytes;
        this.panelLoading = false;
        this.cdr.markForCheck();
      },
      error: (error: unknown) => {
        this.panelLoading = false;
        this.panelRows = [];
        this.panelTotal = 0;
        this.panelError = `Could not load the hosts: ${describeApiError(error)}`;
        this.cdr.markForCheck();
      },
    });
  }

  /** GET /hosts.json. Empty interface / subnet / query mean "all".
   *  Without a sort, the server answers busiest first. */
  private fetchHosts(iface: string, subnet: string, query: string,
                     offset: number, limit: number,
                     sortBy = 'rank', reverse = false) {
    let params = new HttpParams()
      .set('offset', String(offset))
      .set('limit', String(limit))
      .set('sort', sortBy)
      .set('order', reverse ? 'desc' : 'asc');
    if (this.serial) {
      params = params.set('serial', this.serial);
    }
    if (this.range) {
      params = params.set('range', this.range);
    }
    if (iface) {
      params = params.set('interface', iface);
    }
    if (subnet) {
      params = params.set('subnet', subnet);
    }
    if (query) {
      params = params.set('q', query);
    }
    return this.http.get<HostsPage>(`${this.apiBase}/hosts.json`, { params });
  }

  /** Width of a volume bar in the list, 0 to 100. */
  barWidth(row: HostRow): number {
    if (this.panelMaxBytes <= 0) return 0;
    return Math.round((row.bytes / this.panelMaxBytes) * 100);
  }

  /** The policies column of the host list: "#12 LAN out +1". */
  policiesShort(row: HostRow): string {
    const policies = row.policies;
    if (!policies || !policies.top.length) {
      return '-';
    }
    const othersCount = policies.top.length - 1 + policies.other;
    let text = policyText(policies.top[0]);
    if (othersCount > 0) {
      text += ` +${othersCount}`;
    }
    return text;
  }

  /** Every policy of a host of the list, one per line, for the tooltip. */
  policiesTitle(row: HostRow): string {
    const policies = row.policies;
    if (!policies || !policies.top.length) {
      return '';
    }
    const lines: string[] = [];
    for (const policy of policies.top) {
      lines.push(`${policyText(policy)}: ${fmtBytes(policy.bytes)}, to ${policy.to.join(', ') || '?'}`);
    }
    if (policies.other > 0) {
      lines.push(`+${policies.other} other policies, ${fmtBytes(policies.other_bytes)}`);
    }
    return lines.join('\n');
  }

  fmtBytes = fmtBytes;
  trackByIp = (_index: number, row: HostRow): string => row.interface + '|' + row.ip;

  /** Opens an interface and a subnet, even when they sit in a "+N" group. */
  private openPath(iface: NetmapInterface, subnet: NetmapSubnet): void {
    this.openInterface = iface.name;
    this.openSubnet = subnet.cidr;
    const interfaceIndex = this.netmap!.interfaces.indexOf(iface);
    if (interfaceIndex >= INTERFACES_SHOWN) {
      this.showAllInterfaces = true;
    }
    this.showAllSubnets = iface.subnets.indexOf(subnet) >= SUBNETS_SHOWN;
  }

  /* -------------------------------------------------------------- layout */

  /** The icon of an interface, from its configuration. */
  private interfaceIcon(name: string): string {
    const entry = (this.ports?.interfaces ?? []).find((port) => port.name === name);
    if (name.startsWith('ssl.') || entry?.type === 'tunnel') return 'lock';
    if (!entry) return 'switch';
    if (entry.type === 'vlan') return 'tag';
    if (entry.role === 'wan') return 'wan';
    if (entry.role === 'dmz') return 'shield';
    return 'switch';
  }

  private linkOf(name: string): NetmapLink | undefined {
    return this.links.find((link) => link.name === name);
  }

  /** Rebuilds every node and line from the document and what is open. */
  private build(): void {
    this.nodes = [];
    this.edges = [];
    this.policyBadges = [];
    this.policyCard = null;
    const netmap = this.netmap;
    if (!netmap) {
      this.totalText = '';
      return;
    }
    // "in the window" was vague: the volumes cover the session lists read,
    // which the dashboard gives me (coverage)
    this.totalText = `${fmtNum(netmap.total_hosts)} source hosts, `
      + `${fmtBytes(netmap.total_bytes)}`;

    // forget an interface or subnet that isn't in the new document
    const openIface = netmap.interfaces.find((iface) => iface.name === this.openInterface) ?? null;
    if (!openIface) {
      this.openInterface = null;
      this.openSubnet = null;
    }
    let openSubnet: NetmapSubnet | null = null;
    if (openIface) {
      openSubnet = openIface.subnets.find((subnet) => subnet.cidr === this.openSubnet) ?? null;
      const pinned = this.pinned;
      if (!openSubnet && pinned && pinned.interface === openIface.name
          && pinned.subnet === this.openSubnet) {
        // A pinned host in a subnet the document left out (too many
        // subnets): I draw that subnet anyway, with the pinned host alone.
        openSubnet = { cidr: pinned.subnet, bytes: 0, hosts: 0, top: [],
                       other_hosts: 0, other_bytes: 0 };
      }
      if (!openSubnet) {
        this.openSubnet = null;
      }
    }
    const interfaceRows = this.buildInterfaceColumn(netmap);
    let rowCount = interfaceRows;
    if (openIface) {
      const subnetRows = this.buildSubnetColumn(openIface);
      rowCount = Math.max(rowCount, subnetRows);
      if (openSubnet) {
        const hostRows = this.buildHostColumn(openIface, openSubnet);
        rowCount = Math.max(rowCount, hostRows);
      }
    }

    this.mapHeight = Math.max(360, TOP + rowCount * ROW);
    // the firewall sits in front of its interfaces, the internet above it
    this.firewallY = firewallYFor(interfaceRows);
    this.internetY = Math.max(TOP, this.firewallY - 110);
    this.firewallLabel = fitText(this.deviceTitle, FIREWALL_WIDTH - 10, CHAR_WIDTH_LABEL);
    this.modelLabel = fitText(this.model ?? '', FIREWALL_WIDTH - 10, CHAR_WIDTH_MODEL);

    for (const node of this.nodes) {
      this.fitNode(node);
    }

    // internet -> firewall, as thick as the WAN traffic would make it
    this.edges.unshift({
      key: 'internet', width: 7, state: 'normal',
      path: curve(X_INTERNET + 26, this.internetY, X_FIREWALL - FIREWALL_WIDTH / 2, this.firewallY - 10),
    });

    this.buildPolicyBadges(netmap);
    this.buildPolicyCard(netmap);
  }

  /* -------------------------------------------------------- policy card */

  /** One round badge per object that has policies, on the top right
   *  corner of its pill (and of the firewall box, for the whole device). */
  private buildPolicyBadges(netmap: NetmapDoc): void {
    const firewallBadge = this.policyBadge(FIREWALL_KEY, netmap.policies,
                                           X_FIREWALL + FIREWALL_WIDTH / 2 - 10,
                                           this.firewallY - 44, this.deviceTitle);
    if (firewallBadge) {
      this.policyBadges.push(firewallBadge);
    }
    for (const node of this.nodes) {
      const badge = this.policyBadge(node.key, node.policies,
                                     node.x + node.width - 10, node.y - 21,
                                     node.policyTitle ?? node.label);
      if (badge) {
        this.policyBadges.push(badge);
      }
    }
  }

  /** A badge, or null when there's no policy to show. */
  private policyBadge(key: string, policies: NetmapPolicies | undefined,
                      x: number, y: number, what: string): PolicyBadge | null {
    if (!policies) return null;
    const count = policies.top.length + policies.other;
    if (count === 0) return null;
    let text = String(count);
    if (count > 99) {
      text = '99+';
    }
    const word = count === 1 ? 'policy' : 'policies';
    return { key, x, y, text, aria: `${count} ${word} for ${what}: show them` };
  }

  /** The card of the badge I clicked, placed next to it. Closed (null) when
   *  its object isn't on the map any more (a refresh, a collapse). */
  private buildPolicyCard(netmap: NetmapDoc): void {
    const key = this.openPolicyKey;
    if (!key) return;
    const badge = this.policyBadges.find((item) => item.key === key);
    if (!badge) {
      this.openPolicyKey = null;
      return;
    }

    // what the card is about
    let policies = netmap.policies;
    let title = `${this.deviceTitle}, every interface`;
    let fromInterface = '';
    if (key !== FIREWALL_KEY) {
      const node = this.nodes.find((item) => item.key === key);
      if (!node) {
        this.openPolicyKey = null;
        return;
      }
      policies = node.policies;
      title = node.policyTitle ?? node.label;
      fromInterface = node.policyFrom ?? '';
    }
    if (!policies) {
      this.openPolicyKey = null;
      return;
    }

    // the bars are relative to the busiest policy of the card
    let maxBytes = 0;
    for (const policy of policies.top) {
      maxBytes = Math.max(maxBytes, policy.bytes);
    }
    const rows: PolicyRow[] = [];
    for (const policy of policies.top) {
      rows.push(this.policyRow(policy, fromInterface, maxBytes));
    }
    let otherText = '';
    if (policies.other > 0) {
      const word = policies.other === 1 ? 'policy' : 'policies';
      otherText = `+${policies.other} other ${word}, ${fmtBytes(policies.other_bytes)}`;
    }

    // Where it goes: under the badge when the object is in the top half of
    // the map, above it otherwise, so it stays inside the map. It ends a
    // bit right of the badge and never leaves the map on either side.
    const above = badge.y > this.mapHeight / 2;
    let left = badge.x + 30 - CARD_WIDTH;
    left = Math.max(0, Math.min(left, MAP_WIDTH - CARD_WIDTH));
    let top = badge.y + CARD_GAP;
    let room = this.mapHeight - top - 4;
    if (above) {
      top = badge.y - CARD_GAP;
      room = top - 4;
    }

    this.policyCard = {
      key, title, rows, otherText,
      note: rows.length ? '' : 'No policy seen in this window.',
      left, top, above,
      maxHeight: Math.min(CARD_MAX_HEIGHT, room),
    };
  }

  /** One line of the policy card. */
  private policyRow(policy: NetmapPolicy, fromInterface: string, maxBytes: number): PolicyRow {
    const toText = policy.to.length ? namesText(policy.to, ROUTE_NAMES_SHOWN) : '?';

    // the full list of egress interfaces, for the tooltip
    const toTitle = policy.to.length ? policy.to.join(', ') : 'unknown';

    let route = `to ${toText}`;
    let routeTitle = `Out through ${toTitle}`;
    if (fromInterface) {
      route = `${fromInterface} → ${toText}`;
      routeTitle = `In through ${fromInterface}, out through ${toTitle}`;
    }

    let bar = 0;
    if (maxBytes > 0) {
      bar = Math.round((policy.bytes / maxBytes) * 100);
    }
    return {
      key: 'pol:' + policy.id,
      idText: `#${policy.id}`,
      name: policy.name,
      route,
      routeTitle,
      sessions: fmtNum(policy.sessions),
      volume: fmtBytes(policy.bytes),
      bar,
    };
  }

  /** Interfaces column. Returns how many rows it takes. */
  private buildInterfaceColumn(netmap: NetmapDoc): number {
    // Interfaces with no traffic but a link down still show, in red: a
    // WAN that is down is exactly what I want to see on the map.
    const list: NetmapInterface[] = netmap.interfaces.slice();
    for (const link of this.links) {
      const known = list.some((iface) => iface.name === link.name);
      if (!link.link && !known && this.interfaceIcon(link.name) === 'wan') {
        list.push({ name: link.name, bytes: 0, hosts: 0, subnets: [],
                    other_subnets: 0, other_subnets_bytes: 0 });
      }
    }

    let shown = list;
    let hidden: NetmapInterface[] = [];
    if (!this.showAllInterfaces && list.length > INTERFACES_SHOWN + 1) {
      shown = list.slice(0, INTERFACES_SHOWN);
      hidden = list.slice(INTERFACES_SHOWN);
    }
    let maxBytes = 0;
    for (const iface of list) {
      maxBytes = Math.max(maxBytes, iface.bytes);
    }
    const firewallRight = X_FIREWALL + FIREWALL_WIDTH / 2;
    const rows = shown.length + (hidden.length || this.showAllInterfaces ? 1 : 0);
    const firewallY = firewallYFor(rows);

    shown.forEach((iface, index) => {
      const rowY = TOP + index * ROW;
      const link = this.linkOf(iface.name);
      let state: NodeState = 'normal';
      if (link && !link.link) {
        state = 'down';
      } else if (iface.name === this.openInterface) {
        state = 'open';
      }
      let sub = hostCount(iface.hosts);
      if (state === 'down') {
        sub = 'link down';
      }
      // the current rate doesn't fit in the pill, it goes in the tooltip
      let rateText = '';
      if (link && (link.rx_bps != null || link.tx_bps != null)) {
        rateText = `, ${fmtBps((link.rx_bps ?? 0) + (link.tx_bps ?? 0))} now`;
      }
      let action = 'expand';
      if (state === 'open') {
        action = 'collapse';
      } else if (!iface.subnets.length) {
        action = '';
      }
      this.nodes.push({
        key: 'i:' + iface.name, level: 'iface', target: iface.name,
        x: X_IFACE, y: rowY, width: WIDTH_IFACE,
        label: iface.name, sub, volume: fmtBytes(iface.bytes),
        icon: this.interfaceIcon(iface.name), state, action,
        aria: `Interface ${iface.name}, ${sub}${rateText}, ${fmtBytes(iface.bytes)} in the window`,
        mark: '', labelShown: '', subShown: '', title: '',
        policies: iface.policies, policyFrom: iface.name,
        policyTitle: `Interface ${iface.name}`,
      });
      this.edges.push({
        key: 'e:i:' + iface.name, state,
        width: state === 'down' ? 1.5 : lineWidth(iface.bytes, maxBytes),
        path: curve(firewallRight, firewallY, X_IFACE, rowY),
      });
    });

    if (hidden.length || this.showAllInterfaces) {
      let hiddenBytes = 0;
      for (const iface of hidden) {
        hiddenBytes += iface.bytes;
      }
      const rowY = TOP + shown.length * ROW;
      const label = this.showAllInterfaces ? 'fewer interfaces' : `+${hidden.length} interfaces`;
      this.nodes.push({
        key: 'i:' + GROUP_INTERFACES, level: 'iface', target: GROUP_INTERFACES,
        x: X_IFACE, y: rowY, width: WIDTH_IFACE,
        label, sub: this.showAllInterfaces ? '' : 'grouped',
        volume: this.showAllInterfaces ? '' : fmtBytes(hiddenBytes),
        icon: 'group', state: 'group',
        action: this.showAllInterfaces ? 'show less' : 'show all',
        aria: this.showAllInterfaces ? 'Show fewer interfaces' : `Show ${hidden.length} more interfaces`,
        mark: '', labelShown: '', subShown: '', title: '',
      });
      if (!this.showAllInterfaces) {
        this.edges.push({
          key: 'e:i:group', state: 'group', width: lineWidth(hiddenBytes, maxBytes),
          path: curve(firewallRight, firewallY, X_IFACE, rowY),
        });
      }
    }
    return rows;
  }

  /** Subnets column, for the open interface. Returns how many rows it takes. */
  private buildSubnetColumn(iface: NetmapInterface): number {
    const interfaceNode = this.nodes.find((node) => node.target === iface.name);
    // the open interface may sit in the "+N" group: then the lines start there
    const fromY = interfaceNode ? interfaceNode.y : TOP + INTERFACES_SHOWN * ROW;
    const fromX = X_IFACE + WIDTH_IFACE;

    let shown = iface.subnets;
    let hidden: NetmapSubnet[] = [];
    if (!this.showAllSubnets && iface.subnets.length > SUBNETS_SHOWN + 1) {
      shown = iface.subnets.slice(0, SUBNETS_SHOWN);
      hidden = iface.subnets.slice(SUBNETS_SHOWN);
    }
    let maxBytes = 0;
    for (const subnet of iface.subnets) {
      maxBytes = Math.max(maxBytes, subnet.bytes);
    }

    shown.forEach((subnet, index) => {
      const rowY = TOP + index * ROW;
      let state: NodeState = 'normal';
      if (subnet.cidr === this.found) {
        state = 'found';
      } else if (subnet.cidr === this.openSubnet) {
        state = 'open';
      }
      const isOpen = subnet.cidr === this.openSubnet;
      this.nodes.push({
        key: 's:' + subnet.cidr, level: 'subnet', target: subnet.cidr,
        x: X_SUBNET, y: rowY, width: WIDTH_SUBNET,
        label: subnet.cidr, sub: hostCount(subnet.hosts),
        volume: fmtBytes(subnet.bytes), icon: this.subnetIcon(subnet),
        state, action: isOpen ? 'collapse' : 'expand',
        aria: `Subnet ${subnet.cidr}, ${hostCount(subnet.hosts)}, ${fmtBytes(subnet.bytes)}`,
        mark: '', labelShown: '', subShown: '', title: '',
        policies: subnet.policies, policyFrom: iface.name,
        policyTitle: `Subnet ${subnet.cidr} on ${iface.name}`,
      });
      this.edges.push({
        key: 'e:s:' + subnet.cidr, state, width: lineWidth(subnet.bytes, maxBytes),
        path: curve(fromX, fromY, X_SUBNET, rowY),
      });
    });

    // the pinned host's subnet, when the document left it out
    let extraRows = 0;
    const pinned = this.pinned;
    const inDocument = iface.subnets.some((subnet) => subnet.cidr === pinned?.subnet);
    if (pinned && pinned.interface === iface.name && !inDocument) {
      const rowY = TOP + (shown.length + 1) * ROW;
      this.nodes.push({
        key: 's:pin:' + pinned.subnet, level: 'subnet', target: pinned.subnet,
        x: X_SUBNET, y: rowY, width: WIDTH_SUBNET,
        label: pinned.subnet, sub: 'pinned', volume: '', icon: 'unknown',
        state: 'found', action: '',
        aria: `Subnet ${pinned.subnet}, holds the pinned host ${pinned.ip}`,
        mark: '', labelShown: '', subShown: '', title: '',
      });
      this.edges.push({
        key: 'e:s:pin', state: 'found', width: 2,
        path: curve(fromX, fromY, X_SUBNET, rowY),
      });
      extraRows = 2;
    }

    // Everything not drawn one by one: the ones I folded here, plus the ones
    // dashboard.py already left out of the document.
    let groupCount = iface.other_subnets;
    let groupBytes = iface.other_subnets_bytes;
    for (const subnet of hidden) {
      groupCount += 1;
      groupBytes += subnet.bytes;
    }
    const canToggle = hidden.length > 0 || this.showAllSubnets;
    if (groupCount > 0 || this.showAllSubnets) {
      const rowY = TOP + shown.length * ROW;
      let label = `+${groupCount} subnets`;
      if (this.showAllSubnets && groupCount === 0) {
        label = 'fewer subnets';
      }
      let action = '';
      if (canToggle) {
        action = this.showAllSubnets ? 'show less' : 'show all';
      }
      this.nodes.push({
        key: 's:' + GROUP_SUBNETS, level: 'subnet', target: canToggle ? GROUP_SUBNETS : '',
        x: X_SUBNET, y: rowY, width: WIDTH_SUBNET,
        label, sub: groupCount ? 'grouped' : '',
        volume: groupCount ? fmtBytes(groupBytes) : '',
        icon: 'group', state: 'group', action,
        aria: label,
        mark: '', labelShown: '', subShown: '', title: '',
      });
      if (groupCount > 0) {
        this.edges.push({
          key: 'e:s:group', state: 'group', width: lineWidth(groupBytes, maxBytes),
          path: curve(fromX, fromY, X_SUBNET, rowY),
        });
      }
      return Math.max(shown.length + 1, shown.length + extraRows);
    }
    return shown.length + extraRows;
  }

  /** The icon of a subnet: the kind most of its drawn hosts share. */
  private subnetIcon(subnet: NetmapSubnet): string {
    if (subnet.cidr === 'public addresses') return 'cloud';
    const counts: Record<string, number> = {};
    let best = 'unknown';
    for (const host of subnet.top) {
      if (host.kind === 'unknown') continue;
      counts[host.kind] = (counts[host.kind] ?? 0) + 1;
      if (best === 'unknown' || counts[host.kind] > counts[best]) {
        best = host.kind;
      }
    }
    return best;
  }

  /** Hosts column, for the open subnet. Returns how many rows it takes. */
  private buildHostColumn(iface: NetmapInterface, subnet: NetmapSubnet): number {
    const subnetNode = this.nodes.find((node) => node.level === 'subnet' && node.target === subnet.cidr);
    const fromY = subnetNode ? subnetNode.y : TOP + SUBNETS_SHOWN * ROW;
    const fromX = X_SUBNET + WIDTH_SUBNET;
    let maxBytes = 0;
    for (const host of subnet.top) {
      maxBytes = Math.max(maxBytes, host.bytes);
    }
    maxBytes = Math.max(maxBytes, subnet.other_bytes);

    subnet.top.forEach((host, index) => {
      const rowY = TOP + index * ROW;
      const state: NodeState = host.ip === this.found ? 'found' : 'open';
      // a click opens its policy card (only when it has policies)
      let action = '';
      if (host.policies && host.policies.top.length) {
        action = ACTION_HOST_POLICIES;
      }
      this.nodes.push({
        key: 'h:' + host.ip, level: 'host', target: host.ip,
        x: X_HOST, y: rowY, width: WIDTH_HOST,
        label: host.name || host.ip, sub: host.name ? host.ip : '',
        volume: fmtBytes(host.bytes), icon: host.kind, state, action,
        aria: `Host ${host.name || ''} ${host.ip} on ${iface.name}, ${fmtBytes(host.bytes)}`
          + mainPolicyText(host.policies),
        mark: '', labelShown: '', subShown: '', title: '',
        policies: host.policies, policyFrom: iface.name,
        policyTitle: host.name ? `Host ${host.name} (${host.ip})` : `Host ${host.ip}`,
      });
      this.edges.push({
        key: 'e:h:' + host.ip, state, width: lineWidth(host.bytes, maxBytes),
        path: curve(fromX, fromY, X_HOST, rowY),
      });
    });

    if (subnet.other_hosts > 0) {
      const rowY = TOP + subnet.top.length * ROW;
      let label = `+${fmtNum(subnet.other_hosts)} other hosts`;
      if (subnet.other_hosts === 1) {
        label = '+1 other host';
      }
      // the list needs the server (serial); without it there's no action
      const canList = !!this.serial;
      this.nodes.push({
        key: 'h:' + GROUP_HOSTS, level: 'host', target: canList ? GROUP_HOSTS : '',
        x: X_HOST, y: rowY, width: WIDTH_HOST,
        label, sub: `in ${subnet.cidr}`, volume: fmtBytes(subnet.other_bytes),
        icon: 'group', state: 'group', action: canList ? 'show all' : '',
        aria: `${label} in ${subnet.cidr}, ${fmtBytes(subnet.other_bytes)}`,
        mark: '', labelShown: '', subShown: '', title: '',
      });
      this.edges.push({
        key: 'e:h:group', state: 'group', width: lineWidth(subnet.other_bytes, maxBytes),
        path: curve(fromX, fromY, X_HOST, rowY),
      });
    }
    let rows = subnet.top.length + (subnet.other_hosts > 0 ? 1 : 0);

    // the pinned host, when it isn't already drawn among the busiest
    const pinned = this.pinned;
    const alreadyDrawn = subnet.top.some((host) => host.ip === pinned?.ip);
    if (pinned && pinned.interface === iface.name && pinned.subnet === subnet.cidr && !alreadyDrawn) {
      const rowY = TOP + rows * ROW;
      let sub = pinned.name ? pinned.ip : 'pinned';
      if (this.pinnedMissing) {
        sub = 'not seen in this window';
      }
      this.nodes.push({
        key: 'h:pin:' + pinned.ip, level: 'host', target: UNPIN,
        x: X_HOST, y: rowY, width: WIDTH_HOST,
        label: pinned.name || pinned.ip, sub,
        volume: fmtBytes(pinned.bytes), icon: pinned.kind, state: 'found', action: 'unpin',
        aria: `Pinned host ${pinned.name || ''} ${pinned.ip}, ${fmtBytes(pinned.bytes)}`
          + mainPolicyText(pinned.policies),
        mark: '', labelShown: '', subShown: '', title: '',
        policies: pinned.policies, policyFrom: iface.name,
        policyTitle: pinned.name ? `Host ${pinned.name} (${pinned.ip})` : `Host ${pinned.ip}`,
      });
      this.edges.push({
        key: 'e:h:pin', state: 'found', width: 2,
        path: curve(fromX, fromY, X_HOST, rowY),
      });
      rows += 1;
    }
    return rows;
  }

  /** Fills in the texts actually drawn, so nothing runs out of its pill.
   *  Line 1: the label, and the action sign on the right.
   *  Line 2: the sub text, and the volume on the right. */
  private fitNode(node: MapNode): void {
    node.mark = markOf(node.action);
    let labelRoom = node.width - TEXT_LEFT - TEXT_RIGHT;
    if (node.mark) {
      labelRoom -= MARK_ROOM;
    }
    node.labelShown = fitText(node.label, labelRoom, CHAR_WIDTH_LABEL);

    // the volume sits at the end of line 2, the sub text gets what's left
    const volumeWidth = node.volume.length * CHAR_WIDTH_SUB + 10;
    const subRoom = node.width - TEXT_LEFT - TEXT_RIGHT - volumeWidth;
    node.subShown = fitText(node.sub, subRoom, CHAR_WIDTH_SUB);

    // the tooltip says everything, even what was cut
    node.title = node.aria;
    if (node.action) {
      node.title += ' (click to ' + node.action + ')';
    }
  }

  /** Can a node be clicked? Interfaces, subnets, the "+N" toggles, the
   *  "other hosts" list, a pinned host (to unpin it) and a busiest host
   *  (to list its policies). The action word says it for every one. */
  isClickable(node: MapNode): boolean {
    return node.target !== '' && node.action !== '';
  }

  /** The object whose policy card is open, drawn with a thicker outline. */
  isPicked(node: MapNode): boolean {
    return node.key === this.openPolicyKey;
  }

  trackByKey = (_index: number, item: { key: string }): string => item.key;
}
